// Single source of truth for per-locale brand identity.
//
// Every title suffix, og:site_name, JSON-LD publisher/site name and
// alternateName must come from here so the Turkish and English brands can
// never leak into each other again.

export type Locale = "tr" | "en";

export type BrandConfig = {
  /** Brand name used in og:site_name, JSON-LD name and title suffixes. */
  siteName: string;
  /** The other locale's brand name (JSON-LD alternateName). */
  altSiteName: string;
  /** Homepage/default document title for the locale. */
  siteTitle: string;
  /** Canonical origin for the locale (no trailing slash). */
  siteUrl: string;
  /** Default social share image. */
  ogImage: string;
  /** og:locale value. */
  ogLocale: string;
  /** og:locale:alternate value. */
  ogLocaleAlt: string;
  /** <html lang> value. */
  htmlLang: Locale;
};

export const BRAND: Record<Locale, BrandConfig> = {
  tr: {
    siteName: "Akıllı Sürdürülebilirlik",
    altSiteName: "Smart Sustainability",
    siteTitle: "Akıllı Sürdürülebilirlik — İklim, Yapay Zeka ve Gelecek",
    siteUrl: "https://akillisurdurulebilirlik.com",
    ogImage: "https://akillisurdurulebilirlik.com/og-image.jpg",
    ogLocale: "tr_TR",
    ogLocaleAlt: "en_US",
    htmlLang: "tr",
  },
  en: {
    siteName: "Smart Sustainability",
    altSiteName: "Akıllı Sürdürülebilirlik",
    siteTitle: "Smart Sustainability — AI, Climate & Judgment",
    siteUrl: "https://smartsustainability.ai",
    ogImage: "https://smartsustainability.ai/og-image.jpg",
    ogLocale: "en_US",
    ogLocaleAlt: "tr_TR",
    htmlLang: "en",
  },
};

/** Brand name for a locale. */
export function siteName(locale: Locale): string {
  return BRAND[locale].siteName;
}

/**
 * Compose a document title with the locale's brand suffix.
 * Already-suffixed titles pass through unchanged, and the homepage title
 * (no page title given) is the locale's site title.
 */
export function pageTitle(title: string | undefined, locale: Locale): string {
  const brand = BRAND[locale].siteName;
  if (!title) return BRAND[locale].siteTitle;
  const t = title.trim();
  if (t.endsWith(brand)) return t;
  return `${t} | ${brand}`;
}
